import { FC, ChangeEvent } from "react";
import { Controller } from "react-hook-form";
import { LockFill, PencilFill } from "react-bootstrap-icons";

import { CustomInputProps } from "../../types";
import "./styles.css";

const CustomInput: FC<CustomInputProps> = ({
  onInputChange,
  inputType,
  className,
  editable,
  placeholder,
  control,
  inputName,
  rules = {},
  maxLength,
  disableValidation,
  autoFocus,
  onKeyDown,
  inputRef,
  showLock,
  showEditIcon,
}) => {
  const isPhoneField = 
    inputName?.toLowerCase().includes("phone") || 
    inputType?.toLowerCase().includes("phone");

  const handleOnChange = (input: string, onChange: any) => {
    onInputChange(input);
    onChange(input);
  };

  return (
    <Controller
      control={control}
      name={inputName}
      rules={rules}
      render={({
        field: { value, onChange, onBlur },
        fieldState: { error },
      }) => (
        <div
          className="inputContainer"
          onClick={() => inputRef?.current?.focus()}
        >
          <input
            ref={inputRef}
            type={inputType}
            className={`${className} ${error ? "hasError" : ""}`}
            placeholder={placeholder}
            autoComplete="off"
            autoFocus={autoFocus}
            disabled={!editable}
            value={value}
            onKeyDown={onKeyDown}
            onMouseDown={(e) => {
              if (!editable) e.preventDefault();
            }}
            style={{ caretColor: editable ? undefined : "transparent" }}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              let inputValue = e.target.value;

              // Emirates ID masking logic
              if (
                inputName === "residentsEmiratesIdNumber" ||
                inputName === "emiratesidnumber"
              ) {
                const oldValue = value || "";
                const selectionStart = e.target.selectionStart || 0;

                // Check if this was a deletion
                const isDeletion = inputValue.length < oldValue.length;

                // If user deleted a hyphen (or character just before it), 
                // we need to handle it so the hyphen doesn't just "reappear"
                if (isDeletion) {
                  const deletedChar = oldValue[selectionStart];
                  if (deletedChar === "-") {
                    // User deleted a hyphen, so we should delete the digit before it instead
                    const digits = oldValue.replace(/\D/g, "");
                    // Calculate which digit to remove. 
                    // 784-1 -> index 3 is '-', we want to remove the '4' (index 2 in digits)
                    // This is simple: just remove all non-digits from the current (already deleted) inputValue
                    inputValue = inputValue.replace(/\D/g, "");
                  }
                }

                // Remove all non-digits
                let digits = inputValue.replace(/\D/g, "");

                // Truncate to 15 digits
                digits = digits.slice(0, 15);

                // Format: 784-XXXX-XXXXXXX-X
                let formatted = "";
                if (digits.length > 0) {
                  formatted += digits.slice(0, 3);
                  if (digits.length > 3) {
                    formatted += "-" + digits.slice(3, 7);
                  }
                  if (digits.length > 7) {
                    formatted += "-" + digits.slice(7, 14);
                  }
                  if (digits.length > 14) {
                    formatted += "-" + digits.slice(14, 15);
                  }
                }
                inputValue = formatted;

                // Count how many digit characters were before the cursor in the raw typed value
                const digitsBeforeCursor = e.target.value
                  .slice(0, selectionStart)
                  .replace(/\D/g, "").length;

                const target = e.target;
                setTimeout(() => {
                  // Walk the formatted string to find the position after that many digits
                  let digitsSeen = 0;
                  let newCursorPos = formatted.length; // fallback: end of string
                  for (let i = 0; i < formatted.length; i++) {
                    if (formatted[i] !== "-") {
                      digitsSeen++;
                    }
                    if (digitsSeen === digitsBeforeCursor) {
                      newCursorPos = i + 1;
                      break;
                    }
                  }
                  if (digitsBeforeCursor === 0) newCursorPos = 0;
                  target.setSelectionRange(newCursorPos, newCursorPos);
                }, 0);
              }

              // Strip non-digits for phone numbers and apply maxLength
              if (isPhoneField) {
                inputValue = inputValue.replace(/\D/g, "");
                if (maxLength) {
                  inputValue = inputValue.slice(0, maxLength);
                } else {
                  inputValue = inputValue.slice(0, 9);
                }
              }

              handleOnChange(inputValue, onChange);
            }}
            {...(maxLength && isPhoneField ? { maxLength } : {})}
          />
          {showLock && !editable && (
            <div className="lock-icon-container">
              <LockFill size={14} className="lock-icon-svg" />
            </div>
          )}
          {showEditIcon && editable && (
            <div className="edit-icon-container">
              <PencilFill size={12} />
            </div>
          )}
          {value?.length > 0 && isPhoneField && !disableValidation ? (
            <>
              {!value.startsWith("5") ? (
                <div className="errorTextContainer">
                  <div className="errorText">{"invalid mobile number"}</div>
                </div>
              ) : (
                value.length !== 9 && (
                  <div className="errorTextContainer">
                    <div className="errorText">
                      {"Phone number must be exactly 9 digits"}
                    </div>
                  </div>
                )
              )}
            </>
          ) : (
            <>
              {error && (
                <div className="errorTextContainer">
                  <div className="errorText">{typeof error?.message === 'string' ? error.message : ''}</div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    />
  );
};

export default CustomInput;
